// Product gallery: stacked slides crossfade (opacity only). Thumbnails, arrow buttons,
// keyboard arrows and touch/mouse swipe change the slide. When the selected variant
// changes, the product form partial re-renders [data-selected-media-id] and the
// gallery fades to that variant's image.
class SProductGallery extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.hasAttribute('data-active')));
    if (this.slides.length < 2) return;

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.show(i), { signal });
    });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1), { signal });

    this.addEventListener('keydown', (event) => {
      if (!event.target.closest('[data-thumb], [data-stage]')) return;
      const rtl = this.#rtl();
      if (event.key === 'ArrowRight') { event.preventDefault(); this.step(rtl ? -1 : 1, true); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(rtl ? 1 : -1, true); }
    }, { signal });

    const stage = this.querySelector('[data-stage]');
    let startX = 0;
    let startY = 0;
    let tracking = false;
    stage?.addEventListener('pointerdown', (event) => {
      tracking = true;
      startX = event.clientX;
      startY = event.clientY;
    }, { signal });
    stage?.addEventListener('pointerup', (event) => {
      if (!tracking) return;
      tracking = false;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const forward = this.#rtl() ? dx > 0 : dx < 0;
      this.step(forward ? 1 : -1);
    }, { signal });
    stage?.addEventListener('pointercancel', () => { tracking = false; }, { signal });
    stage?.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    const form = document.querySelector('[data-product-form]');
    if (form) {
      this.observer = new MutationObserver(() => {
        const mediaId = form.querySelector('[data-selected-media-id]')?.dataset.selectedMediaId;
        if (!mediaId || mediaId === this.lastMediaId) return;
        this.lastMediaId = mediaId;
        const target = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (target > -1) this.show(target);
      });
      this.lastMediaId = form.querySelector('[data-selected-media-id]')?.dataset.selectedMediaId;
      this.observer.observe(form, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  step(delta, focusThumb = false) {
    const count = this.slides.length;
    this.show((this.index + delta + count) % count);
    if (focusThumb) this.thumbs[this.index]?.focus();
  }

  show(next) {
    if (next === this.index) return;
    this.slides[this.index]?.querySelectorAll('video').forEach((video) => video.pause());
    this.index = next;
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.thumbs.forEach((thumb, i) => {
      const active = i === next;
      thumb.toggleAttribute('data-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = `${next + 1} / ${this.slides.length}`;
  }

  #rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }
}

customElements.define('s-product-gallery', SProductGallery);
